/* Shared metadata + helpers for the registry-driven agent surfaces.
 * Maps registry lucide icon slugs -> react-icons/lu components, group ordering,
 * group accent classes, and tier helpers. Keep this the single source of truth
 * so AgentsShowcase, the mega-menu, and /pricing all stay in sync. */
import * as LuIcons from 'react-icons/lu';
import { LuBox } from 'react-icons/lu';
import registry from './agentsRegistry.json';
import pricing from './agentPricing.json';

/* lucide slug (registry "icon") -> react-icons/lu PascalCase component name */
const slugToLu = (slug) => {
  if (!slug) return null;
  const pascal =
    'Lu' +
    slug
      .split('-')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join('');
  return LuIcons[pascal] || null;
};

/* Targeted fallbacks for slugs whose lucide name differs in this react-icons build */
const ICON_OVERRIDES = {
  'share-2': LuIcons.LuShare2,
  'message-square': LuIcons.LuMessageSquare,
  'message-circle': LuIcons.LuMessageCircle,
  'file-text': LuIcons.LuFileText,
  'book-open': LuIcons.LuBookOpen,
  'book-marked': LuIcons.LuBookMarked,
  'scan-text': LuIcons.LuScanText,
  'pen-line': LuIcons.LuPenLine,
  'library-big': LuIcons.LuLibraryBig,
  'spell-check': LuIcons.LuSpellCheck,
  'file-input': LuIcons.LuFileInput,
};

export const iconFor = (slug) => ICON_OVERRIDES[slug] || slugToLu(slug) || LuBox;

/* Order groups so the Authoring (ManuscriptSmith) agents lead, then capability areas */
export const GROUP_ORDER = [
  'Authoring',
  'Content & SEO',
  'Distribution',
  'Outreach & CRM',
  'Media & Design',
  'Shared Services',
];

export const GROUP_META = {
  Authoring: { short: 'Writing Studio', blurb: 'Write, analyze and ship your book in your own voice.' },
  'Content & SEO': { short: 'Content Engine', blurb: 'Idea to draft to fact-check to SEO to images.' },
  Distribution: { short: 'Social & Links', blurb: 'On-brand posts, branded links, reach.' },
  'Outreach & CRM': { short: 'Outreach & CRM', blurb: 'Enrichment, LinkedIn, email, SMS, call intel.' },
  'Media & Design': { short: 'Media & Design', blurb: 'Covers, video, flipbooks and brand assets.' },
  'Shared Services': { short: 'Platform', blurb: 'Chatbot/RAG, knowledge, system and gateway.' },
};

export const TIERS = ['Author', 'Pro'];
export const TIER_PRICE = { Author: '$19', Pro: '$49' };
export const TIER_RANK = { Free: 1, Author: 1, Pro: 2 }; // legacy 'Free' registry entries count as Author (no free tier)

/* status -> badge styling + label */
export const STATUS_META = {
  live: { label: 'Live', cls: 'bg-green-100 text-green-700' },
  beta: { label: 'Beta', cls: 'bg-amber-100 text-amber-700' },
  planned: { label: 'Planned', cls: 'bg-gray-100 text-gray-500' },
};

const priceById = {};
pricing.agents.forEach((a) => {
  priceById[a.id] = a;
});

/* Normalize a registry record into a render-ready shape */
export const agents = registry.map((a) => {
  const p = a.pricingId ? priceById[a.pricingId] : null;
  return {
    ...a,
    minTier: ((a.minTier || (p && p.minTier) || 'Pro') === 'Free' ? 'Author' : (a.minTier || (p && p.minTier) || 'Pro')),
    addonPriceUsd: a.addonPriceUsd ?? (p ? p.addonPriceUsd : null),
    Icon: iconFor(a.icon),
  };
});

/* Group helper, in display order, skipping empty groups */
export const agentsByGroup = () =>
  GROUP_ORDER.map((g) => ({
    group: g,
    meta: GROUP_META[g] || { short: g, blurb: '' },
    items: agents.filter((a) => a.group === g),
  })).filter((g) => g.items.length);

/* "Recently added" = the Authoring agents (newest into the registry) */
export const recentlyAddedIds = registry
  .filter((a) => a.group === 'Authoring')
  .map((a) => a.id);

/* Whether an agent is included at a given tier (cumulative ladder), or add-on only */
export const includedInTier = (agent, tier) => {
  if (agent.addonPriceUsd != null && TIER_RANK[agent.minTier] > TIER_RANK[tier]) {
    return 'addon';
  }
  return TIER_RANK[tier] >= TIER_RANK[agent.minTier];
};
